import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import {
  createOrder,
  getOrders,
  updateOrderStatus,
  // getOrderStatus,
  getUserOrders,
  getOrderById,
} from "../../../services/order";
import { useState } from "react";
import { useAuth } from "../../../utils/AuthContext";

function useOrders(orderId) {
  const queryClient = useQueryClient();
  const [orderSuccess, setOrderSuccess] = useState(false);
  const { isLoggedIn, user } = useAuth();
  // Get all orders query ("ONLY FOR ADMIN")
  const {
    data: orders,
    isLoading: isGettingOrders,
    error: ordersError,
  } = useQuery({
    queryKey: ["orders"],
    queryFn: () => {
      // console.log("I was triggered Im admin fn");
      return getOrders();
    },
    enabled: !!user && +user?.role === 1,
  });

  // Get user orders query ("FOR USERS AND ADMIN")
  const {
    data: userOrders,
    isLoading: isGettingUserOrders,
    error: userOrdersError,
  } = useQuery({
    queryKey: ["userOrders"],
    queryFn: () => {
      return getUserOrders();
    },
    enabled: !!isLoggedIn,
  });

  // Get order status query ("FOR USER AND ADMIN")
  // const {
  //   data: orderStatus,
  //   isLoading: isGettingOrderStatus,
  //   error: orderStatusError,
  // } = useQuery({
  //   queryKey: ["orderStatus"],
  //   queryFn: () => getOrderStatus(),
  // });

  // Create order mutation ("FOR USER")
  const { mutate: createOrderFn, isLoading: isCreatingOrder } = useMutation({
    mutationFn: (order) => {
      // console.log("order mutation fn: ", order);
      return createOrder(order);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["orders", "userOrders"]);
      setOrderSuccess(true);
      toast.success("Order created successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Error creating order");
    },
  });

  // Update order status mutation ("FOR ADMIN ONLY")
  const { mutate: updateOrderStatusFn, isLoading: isUpdatingStatus } =
    useMutation({
      mutationFn: ({ orderId, status }) => updateOrderStatus(orderId, status),
      onMutate: async ({ orderId, status }) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries(["orders"]);

        // Snapshot the previous value
        const previousOrders = queryClient.getQueryData(["orders"]);

        // Optimistically update to the new value
        queryClient.setQueryData(["orders"], (old) => ({
          ...old,
          data: old.data.map((order) =>
            order._id === orderId ? { ...order, status } : order
          ),
        }));

        // Return a context object with the snapshotted value
        return { previousOrders };
      },
      onError: (err, newOrder, context) => {
        // If the mutation fails, use the context returned from onMutate to roll back
        queryClient.setQueryData(["orders"], context.previousOrders);
        toast.error(
          err.response?.data?.message || "Error updating order status"
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["orders", "userOrders"]);
        toast.success("Order status updated successfully");
      },
    });

  // GET ORDER BY ID
  const {
    data: orderIdData,
    isLoading: isGettingOrderById,
    isError: isErrorGettingOrderById,
  } = useQuery({
    queryKey: ["order", orderId],
    queryFn: () => getOrderById(orderId),
    enabled: !!orderId,
  });

  return {
    // CREATE ORDER
    createOrderFn,
    isCreatingOrder,
    orderSuccess,
    // GET ALL ORDERS
    orders: orders?.data || [],
    isGettingOrders,
    ordersError: ordersError?.response?.data?.message || ordersError?.message,
    // GET ONE USER'S ORDERS
    userOrders: userOrders?.data || [],
    isGettingUserOrders,
    userOrdersError:
      userOrdersError?.response?.data?.error || userOrdersError?.message,
    // GET ALL POSSIBLE STATUS FOR ORDER
    // orderStatus: orderStatus?.data || [],
    // isGettingOrderStatus,
    // orderStatusError:
    //   orderStatusError?.response?.data?.error || orderStatusError?.message,
    // UPDATE ORDER STATUS
    isUpdatingStatus,
    updateOrderStatusFn,
    // GET ORDER BY ID
    orderIdData,
    isGettingOrderById,
    isErrorGettingOrderById,
  };
}

export default useOrders;
